import type { FC } from "react";
import { useForm } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import { FieldInput, FieldRadio, FieldSelect } from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import RiskModalFooter from "@/components/modals/profil-risk/risk-modal-footer";
import type { RiskUpdateModalProps } from "@/components/modals/profil-risk/risk-update-modal";
import { Separator } from "@/components/ui/separator";
import {
  patchBrushingDefaultValues,
  patchBrushingSchema,
} from "@/schemas/patient";
import { resetForm, yesNoRadioOptions } from "@/utils/form";
import {
  stateOptions,
  toothBrushHairTypeOptions,
  toothBrushTypeOptions,
} from "@/utils/patient";

const ToothbrushModal: FC<RiskUpdateModalProps> = ({
  riskModalOpen,
  setRiskModalOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const {
    toothState,
    toothBrush,
    toothBrushHair,
    toothPaste,
    brushingAccessories,
    brushingFrequency,
    mouthWash,
    interdentalSystem,
  } = patient;

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      toothState: toothState,
      toothBrush: toothBrush ?? patchBrushingDefaultValues.toothBrush,
      toothBrushHair:
        toothBrushHair ?? patchBrushingDefaultValues.toothBrushHair,
      toothPaste: toothPaste ?? patchBrushingDefaultValues.toothPaste,
      brushingAccessories:
        brushingAccessories ?? patchBrushingDefaultValues.brushingAccessories,
      brushingFrequency:
        brushingFrequency ?? patchBrushingDefaultValues.brushingFrequency,
      mouthWash: mouthWash ?? patchBrushingDefaultValues.mouthWash,
      interdentalSystem,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchBrushingSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          setRiskModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      description={
        <FieldSelect
          Field={Field}
          schema={patchBrushingSchema}
          name="toothState"
          options={stateOptions}
          validators={{
            onChange: patchBrushingSchema.shape.toothState,
          }}
          risk={"toothbrush"}
        />
      }
      open={riskModalOpen !== "none"}
      onOpenChange={() => setRiskModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <div className="flex gap-4">
          <FieldSelect
            Field={Field}
            schema={patchBrushingSchema}
            name="toothBrush"
            label="Type de brosse à dents"
            options={toothBrushTypeOptions}
            validators={{
              onChange: patchBrushingSchema.shape.toothBrush,
            }}
          />
          <FieldSelect
            Field={Field}
            schema={patchBrushingSchema}
            name="toothBrushHair"
            label="Poils de brosse à dents"
            options={toothBrushHairTypeOptions}
            validators={{
              onChange: patchBrushingSchema.shape.toothBrushHair,
            }}
          />
          <FieldInput
            Field={Field}
            schema={patchBrushingSchema}
            name="brushingFrequency"
            placeholder="0"
            label="Fréquence"
            validators={{
              onChange: patchBrushingSchema.shape.brushingFrequency,
            }}
          />
        </div>
        <div className="flex gap-4">
          <FieldRadio
            Field={Field}
            schema={patchBrushingSchema}
            name="interdentalSystem"
            label="Système interdentaire"
            options={yesNoRadioOptions}
            type="boolean"
            orientation="horizontal"
            validators={{
              onChange: patchBrushingSchema.shape.interdentalSystem,
            }}
          />
          <FieldInput
            Field={Field}
            schema={patchBrushingSchema}
            name="brushingAccessories"
            placeholder="Ajouter des accessoires"
            label="Accessoires de brossage"
            validators={{
              onChange: patchBrushingSchema.shape.brushingAccessories,
            }}
          />
        </div>
        <div className="flex gap-4">
          <FieldInput
            Field={Field}
            schema={patchBrushingSchema}
            name="toothPaste"
            placeholder="Dentifrice"
            label="Dentifrice"
            validators={{
              onChange: patchBrushingSchema.shape.toothPaste,
            }}
          />
          <FieldInput
            Field={Field}
            schema={patchBrushingSchema}
            name="mouthWash"
            placeholder="Bain de bouche"
            label="Bain de bouche"
            validators={{
              onChange: patchBrushingSchema.shape.mouthWash,
            }}
          />
        </div>
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <RiskModalFooter
        setRiskModalOpen={setRiskModalOpen}
        handleSubmit={handleSubmit}
      />
    </DialogCustom>
  );
};

export default ToothbrushModal;
